import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  UsageChainVerification,
  UsageEvent,
  UsageEventView,
  UsageKind,
  UsageQuery,
  UsageSeriesPoint,
  UsageSummaryView
} from '../api.js';
import type { AdminHttpClient } from '../http.js';
import { DataTable, type DataColumn } from '../components/DataTable.js';
import { Metric } from '../components/Metric.js';
import { useFetchOnMount } from '../hooks/useFetchOnMount.js';

const DEFAULT_DAYS = 7;
const PAGE_SIZE = 50;
const RANGE_WARNING_DAYS = 90;
const USAGE_COLUMN_STORAGE_KEY = 'veluga-admin-usage-cols';

type UsageColumnKey =
  | 'created'
  | 'user'
  | 'userEmail'
  | 'model'
  | 'backend'
  | 'routeMode'
  | 'usageKind'
  | 'inputTokens'
  | 'cachedInputTokens'
  | 'cacheWriteInputTokens'
  | 'cachedUnits'
  | 'cacheWriteUnits'
  | 'nonCachedUnits'
  | 'cacheStatus'
  | 'outputTokens'
  | 'totalTokens'
  | 'webSearchRequests'
  | 'imageInputCount'
  | 'imageInputBytes'
  | 'imageGenerationCount'
  | 'videoGenerationSeconds'
  | 'generationVariantKey'
  | 'mediaRouteId'
  | 'mediaModelId'
  | 'credits'
  | 'rateVersion'
  | 'conversationId'
  | 'sessionId'
  | 'requestId'
  | 'hash';

const DEFAULT_USAGE_COLUMNS: UsageColumnKey[] = [
  'created',
  'user',
  'model',
  'usageKind',
  'imageGenerationCount',
  'videoGenerationSeconds',
  'generationVariantKey',
  'totalTokens',
  'cacheStatus',
  'credits',
  'hash'
];
const USAGE_COLUMN_KEYS = new Set<UsageColumnKey>([
  'created',
  'user',
  'userEmail',
  'model',
  'backend',
  'routeMode',
  'usageKind',
  'inputTokens',
  'cachedInputTokens',
  'cacheWriteInputTokens',
  'cachedUnits',
  'cacheWriteUnits',
  'nonCachedUnits',
  'cacheStatus',
  'outputTokens',
  'totalTokens',
  'webSearchRequests',
  'imageInputCount',
  'imageInputBytes',
  'imageGenerationCount',
  'videoGenerationSeconds',
  'generationVariantKey',
  'mediaRouteId',
  'mediaModelId',
  'credits',
  'rateVersion',
  'conversationId',
  'sessionId',
  'requestId',
  'hash'
]);
type UsageEventsResult = Awaited<ReturnType<AdminHttpClient['usageEvents']>>;
type UsageLoadResult = {
  aggregateResult: PromiseSettledResult<[UsageSummaryView, UsageSeriesPoint[]]>;
  eventsResult: PromiseSettledResult<UsageEventsResult>;
  page: number;
};

export function UsageScreen({ http }: { http: AdminHttpClient }) {
  const { t } = useTranslation();
  const [from, setFrom] = useState(() => inputDateTime(new Date(Date.now() - DEFAULT_DAYS * 24 * 60 * 60 * 1000)));
  const [to, setTo] = useState(() => inputDateTime(new Date()));
  const [granularity, setGranularity] = useState<'hour' | 'day' | 'month'>('day');
  const [tz, setTz] = useState('UTC');
  const [userId, setUserId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [modelId, setModelId] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [usageKind, setUsageKind] = useState<UsageKind | ''>('');
  const [hasImageInput, setHasImageInput] = useState(false);
  const [summary, setSummary] = useState<UsageSummaryView>();
  const [series, setSeries] = useState<UsageSeriesPoint[]>([]);
  const [events, setEvents] = useState<UsageEventView[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [page, setPage] = useState(0);
  const [pageCursors, setPageCursors] = useState<Record<number, number>>({});
  const [error, setError] = useState<string>();
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<UsageColumnKey[]>(readUsageColumns);
  const [verification, setVerification] = useState<UsageChainVerification>();
  const [verifying, setVerifying] = useState(false);

  const query = useMemo<UsageQuery>(
    () => ({
      ...(from ? { from: new Date(from).toISOString() } : {}),
      ...(to ? { to: new Date(to).toISOString() } : {}),
      ...(userId.trim() ? { userId: userId.trim() } : {}),
      ...(groupId.trim() ? { groupId: groupId.trim() } : {}),
      ...(modelId.trim() ? { modelId: modelId.trim() } : {}),
      ...(conversationId.trim() ? { conversationId: conversationId.trim() } : {}),
      ...(usageKind ? { usageKind } : {}),
      ...(hasImageInput ? { hasImageInput } : {}),
    }),
    [from, to, userId, groupId, modelId, conversationId, usageKind, hasImageInput]
  );
  const afterSeq = pageCursors[page];
  const rangeDays = useMemo(() => daysBetween(from, to), [from, to]);
  const showRangeWarning = rangeDays > RANGE_WARNING_DAYS;
  const rangeLabel = useMemo(
    () =>
      formatDateRangeLabel(from, to, {
        from: t('usage.from'),
        to: t('usage.to'),
        allTime: t('usage.allTime')
      }),
    [from, to, t]
  );

  const usageColumns = useMemo<Array<DataColumn<UsageEventView> & { key: UsageColumnKey }>>(
    () => [
      { key: 'created', header: t('usage.created'), render: (event) => new Date(event.createdAt).toLocaleString() },
      { key: 'user', header: t('usage.user'), render: (event) => event.userId },
      { key: 'userEmail', header: t('usage.userEmail'), render: (event) => event.userEmail ?? '—' },
      { key: 'model', header: t('usage.model'), render: (event) => event.catalogModelId ?? event.mediaModelId ?? t('common.none') },
      { key: 'backend', header: t('usage.backend'), render: (event) => event.backendId ?? t('common.none') },
      { key: 'routeMode', header: t('usage.routeMode'), render: (event) => event.routeMode ?? t('common.none') },
      { key: 'usageKind', header: t('usage.usageKind'), render: (event) => event.usageKind ?? 'llm' },
      { key: 'inputTokens', header: t('usage.inputTokens'), render: (event) => formatInt(event.inputTokens) },
      { key: 'cachedInputTokens', header: t('usage.cachedInputTokens'), render: (event) => event.cache?.cachedInputTokens == null ? t('common.none') : formatInt(event.cache.cachedInputTokens) },
      { key: 'cacheWriteInputTokens', header: t('usage.cacheWriteInputTokens'), render: (event) => event.cache?.cacheWriteInputTokens == null ? t('common.none') : formatInt(event.cache.cacheWriteInputTokens) },
      { key: 'cachedUnits', header: t('usage.cachedUnits'), render: (event) => formatCreditComponentUnits(event, 'cachedUnits', t('common.none')) },
      { key: 'cacheWriteUnits', header: t('usage.cacheWriteUnits'), render: (event) => formatCreditComponentUnits(event, 'cacheWriteUnits', t('common.none')) },
      { key: 'nonCachedUnits', header: t('usage.nonCachedUnits'), render: (event) => formatCreditComponentUnits(event, 'nonCachedUnits', t('common.none')) },
      { key: 'cacheStatus', header: t('usage.cacheStatus'), render: (event) => (event.usageKind ?? 'llm') === 'llm' ? t(`usage.cache_${event.cache?.cacheStatus ?? 'unknown'}`) : t('common.none') },
      { key: 'outputTokens', header: t('usage.outputTokens'), render: (event) => formatInt(event.outputTokens) },
      { key: 'totalTokens', header: t('usage.tokens'), render: (event) => formatInt(event.totalTokens) },
      { key: 'webSearchRequests', header: t('usage.webSearchRequests'), render: (event) => event.webSearchRequests == null ? t('common.none') : formatInt(event.webSearchRequests) },
      { key: 'imageInputCount', header: t('usage.imageInputCount'), render: (event) => event.imageInputCount == null ? t('common.none') : formatInt(event.imageInputCount) },
      { key: 'imageInputBytes', header: t('usage.imageInputBytes'), render: (event) => event.imageInputBytes == null ? t('common.none') : formatInt(event.imageInputBytes) },
      { key: 'imageGenerationCount', header: t('usage.imageGenerationCount'), render: (event) => event.imageGenerationCount == null ? t('common.none') : formatInt(event.imageGenerationCount) },
      { key: 'videoGenerationSeconds', header: t('usage.videoGenerationSeconds'), render: (event) => event.videoGenerationSeconds == null ? t('common.none') : formatInt(event.videoGenerationSeconds) },
      { key: 'generationVariantKey', header: t('usage.generationVariantKey'), render: (event) => event.generationVariantKey ?? t('common.none') },
      { key: 'mediaRouteId', header: t('usage.mediaRouteId'), render: (event) => event.mediaRouteId ?? t('common.none') },
      { key: 'mediaModelId', header: t('usage.mediaModelId'), render: (event) => event.mediaModelId ?? t('common.none') },
      { key: 'credits', header: t('usage.credits'), render: (event) => formatCredits(displayCreditMillis(event)) },
      { key: 'rateVersion', header: t('usage.rateVersion'), render: (event) => event.credit?.rateVersion ?? t('common.none') },
      { key: 'conversationId', header: t('usage.conversationId'), render: (event) => event.conversationId ? <code>{event.conversationId}</code> : t('common.none') },
      { key: 'sessionId', header: t('usage.session'), render: (event) => <code>{event.sessionId}</code> },
      { key: 'requestId', header: t('usage.requestId'), render: (event) => <code>{event.requestId}</code> },
      { key: 'hash', header: t('usage.hash'), render: (event) => <code>{event.eventHash ?? event.id}</code> }
    ],
    [t]
  );
  const selectedColumns = useMemo(() => {
    const selected = new Set(selectedColumnKeys);
    return usageColumns.filter((column) => selected.has(column.key));
  }, [selectedColumnKeys, usageColumns]);

  const fetchUsage = useCallback(
    async (_signal: AbortSignal): Promise<UsageLoadResult> => {
      setError(undefined);
      const [aggregateResult, eventsResult] = await Promise.allSettled([
        Promise.all([http.usageSummary(query), http.usageSeries({ ...query, granularity, tz })]),
        http.usageEvents({ ...query, limit: PAGE_SIZE, ...(afterSeq !== undefined ? { afterSeq } : {}) })
      ]);
      return { aggregateResult, eventsResult, page };
    },
    [afterSeq, granularity, http, page, query, tz]
  );

  const onUsageLoaded = useCallback((result: UsageLoadResult) => {
    if (result.aggregateResult.status === 'fulfilled') {
      const [nextSummary, nextSeries] = result.aggregateResult.value;
      setSummary(nextSummary);
      setSeries(nextSeries);
    } else {
      setSummary(undefined);
      setSeries([]);
      setError(usageErrorCode(result.aggregateResult.reason));
    }

    if (result.eventsResult.status === 'fulfilled') {
      setEvents(result.eventsResult.value.items);
      setTotalEvents(result.eventsResult.value.total);
      const lastSeq = result.eventsResult.value.items.at(-1)?.seq;
      if (lastSeq !== undefined) {
        setPageCursors((current) => ({ ...current, [result.page + 1]: lastSeq }));
      }
    } else {
      setEvents([]);
      setTotalEvents(0);
      if (result.aggregateResult.status === 'fulfilled') setError(usageErrorCode(result.eventsResult.reason));
    }
  }, []);

  const onUsageError = useCallback((loadError: unknown) => {
    setSummary(undefined);
    setSeries([]);
    setEvents([]);
    setTotalEvents(0);
    setError(usageErrorCode(loadError));
  }, []);

  useFetchOnMount({ fetcher: fetchUsage, onData: onUsageLoaded, onError: onUsageError });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(USAGE_COLUMN_STORAGE_KEY, JSON.stringify(selectedColumnKeys));
  }, [selectedColumnKeys]);

  useEffect(() => {
    setPage(0);
    setPageCursors({});
    // The chain result is window/filter-scoped; invalidate it when scope changes.
    setVerification(undefined);
  }, [query, granularity, tz]);

  async function exportCsv() {
    setError(undefined);
    try {
      const csv = await http.usageExport(query);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'veluga-usage.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(usageErrorCode(exportError));
    }
  }

  async function verifyChain() {
    setError(undefined);
    setVerifying(true);
    try {
      setVerification(await http.usageVerify(query));
    } catch (verifyError) {
      setVerification(undefined);
      setError(usageErrorCode(verifyError));
    } finally {
      setVerifying(false);
    }
  }

  function toggleColumn(key: UsageColumnKey) {
    setSelectedColumnKeys((current) => {
      if (current.includes(key)) return current.length === 1 ? current : current.filter((columnKey) => columnKey !== key);
      return [...current, key];
    });
  }

  function shrinkRange() {
    const end = to ? new Date(to) : new Date();
    setTo(inputDateTime(end));
    setFrom(inputDateTime(new Date(end.getTime() - DEFAULT_DAYS * 24 * 60 * 60 * 1000)));
  }

  function changeUsageKind(next: UsageKind | '') {
    setUsageKind(next);
    if (next === 'image_generation' || next === 'video_generation') setHasImageInput(false);
  }

  const generationKindSelected = usageKind === 'image_generation' || usageKind === 'video_generation';
  const seriesUnit =
    usageKind === 'realtime_voice' ? t('usage.voiceSessions')
      : usageKind === 'speech_to_text' ? t('usage.speechToTextRequests')
      : usageKind === 'text_to_speech' ? t('usage.textToSpeechRequests')
      : usageKind === 'web_search'
      ? t('usage.webSearchRequests')
      : usageKind === 'image_generation'
        ? t('usage.imageGenerationCount')
        : usageKind === 'video_generation'
          ? t('usage.videoGenerationSeconds')
          : t('usage.tokens');

  return (
    <section className="content-stack">
      <div className="usage-dashboard-summary">
        <div className="metric-grid">
          <Metric label={t('usage.requests')} value={formatInt(summary?.requests ?? 0)} />
          <Metric label={t('usage.tokens')} value={formatInt(summary?.totalTokens ?? 0)} />
          <Metric label={t('usage.cachedInputTokens')} value={formatInt(summary?.cachedInputTokens ?? 0)} />
          <Metric label={t('usage.cacheWriteInputTokens')} value={formatInt(summary?.cacheWriteInputTokens ?? 0)} />
          <Metric label={t('usage.cacheKnownRequests')} value={formatInt(summary?.cacheKnownRequests ?? 0)} />
          <Metric label={t('usage.cacheUnknownRequests')} value={formatInt(summary?.cacheUnknownRequests ?? 0)} />
          <Metric label={t('usage.cacheTokenRatio')} value={formatPercent(summary?.cacheTokenRatio ?? 0)} />
          <Metric label={t('usage.webSearchRequests')} value={formatInt(summary?.webSearchRequests ?? 0)} />
          <Metric label={t('usage.imageCount')} value={formatInt(summary?.imageInputCount ?? 0)} />
          <Metric label={t('usage.imageGenerationCount')} value={formatInt(summary?.imageGenerationCount ?? 0)} />
          <Metric label={t('usage.videoGenerationSeconds')} value={formatInt(summary?.videoGenerationSeconds ?? 0)} />
          <Metric label={t('usage.videoGenerationCredits')} value={formatCredits(summary?.videoGenerationCreditMillis ?? 0)} />
          <Metric label={t('usage.voiceSessions')} value={formatInt(summary?.realtimeVoiceSessions ?? 0)} />
          <Metric label={t('usage.voiceCredits')} value={formatCredits(summary?.realtimeVoiceCreditMillis ?? 0)} />
          <Metric label={t('usage.speechToTextRequests')} value={formatInt(summary?.speechToTextRequests ?? 0)} />
          <Metric label={t('usage.speechToTextCredits')} value={formatCredits(summary?.speechToTextCreditMillis ?? 0)} />
          <Metric label={t('usage.textToSpeechRequests')} value={formatInt(summary?.textToSpeechRequests ?? 0)} />
          <Metric label={t('usage.textToSpeechCredits')} value={formatCredits(summary?.textToSpeechCreditMillis ?? 0)} />
          <Metric label={t('usage.credits')} value={formatCredits(summary?.totalCreditMillis ?? 0)} />
        </div>
        <section className="panel">
          <h2>{t('usage.usageOverTime')}</h2>
          <UsageBars points={series} usageKind={usageKind} unit={seriesUnit} empty={t('usage.noUsage')} />
        </section>
      </div>
      <p className="screen-help">{t('usage.cacheCreditNotice')}</p>
      <p className="screen-help">{t('screenHelp.usage')}</p>
      <div className="toolbar-form usage-toolbar">
        <label>
          {t('usage.from')}
          <input type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          {t('usage.to')}
          <input type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <label>
          {t('usage.granularity')}
          <select value={granularity} onChange={(event) => setGranularity(event.target.value as typeof granularity)}>
            <option value="hour">{t('usage.hour')}</option>
            <option value="day">{t('usage.day')}</option>
            <option value="month">{t('usage.month')}</option>
          </select>
        </label>
        <label>
          {t('usage.timezone')}
          <input value={tz} onChange={(event) => setTz(event.target.value)} />
        </label>
        <label>
          {t('usage.user')}
          <input value={userId} onChange={(event) => setUserId(event.target.value)} />
        </label>
        <label>
          {t('usage.group')}
          <input value={groupId} onChange={(event) => setGroupId(event.target.value)} />
        </label>
        <label>
          {t('usage.model')}
          <input value={modelId} onChange={(event) => setModelId(event.target.value)} />
        </label>
        <label>
          {t('usage.conversationId')}
          <input value={conversationId} onChange={(event) => setConversationId(event.target.value)} />
        </label>
        <label>
          {t('usage.usageKind')}
          <select value={usageKind} onChange={(event) => changeUsageKind(event.target.value as UsageKind | '')}>
            <option value="">{t('usage.kindAll')}</option>
            <option value="llm">{t('usage.kindLlm')}</option>
            <option value="route_judge">{t('usage.kindRouteJudge')}</option>
            <option value="web_search">{t('usage.kindWebSearch')}</option>
            <option value="image_generation">{t('usage.kindImageGeneration')}</option>
            <option value="realtime_voice">{t('usage.kindVoice')}</option>
            <option value="speech_to_text">{t('usage.kindSpeechToText')}</option>
            <option value="text_to_speech">{t('usage.kindTextToSpeech')}</option>
            <option value="video_generation">{t('usage.kindVideoGeneration')}</option>
          </select>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={hasImageInput}
            disabled={generationKindSelected}
            onChange={(event) => setHasImageInput(event.target.checked)}
          />
          <span>{t('usage.hasImageInput')}</span>
        </label>
        <button type="button" onClick={exportCsv}>
          {t('usage.exportCsv')}
        </button>
        <button type="button" onClick={verifyChain} disabled={verifying}>
          {verifying ? t('usage.verifyingChain') : t('usage.verifyChain')}
        </button>
      </div>
      {verification ? (
        <div className={verification.valid ? 'usage-chain-ok' : 'error'}>
          <strong>
            {verification.valid ? t('usage.chainValid') : t('usage.chainInvalid')}
          </strong>{' '}
          <span>{t('usage.chainChecked', { count: verification.checkedEvents })}</span>
          {verification.violations.length > 0 ? (
            <ul className="usage-chain-violations">
              {verification.violations.map((violation) => (
                <li key={`${violation.requestId}:${violation.reason}`}>
                  <code>{violation.requestId}</code>
                  {violation.seq != null ? ` (seq ${violation.seq})` : ''} — {t(`usage.chainReason.${violation.reason}`)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div className="usage-scope">
        <strong>{t('usage.scopeLabel')}</strong>
        <span>{t('usage.scopeValue', { range: rangeLabel, timezone: tz })}</span>
      </div>
      {showRangeWarning ? <div className="warning">{t('usage.rangeWarning', { days: RANGE_WARNING_DAYS })}</div> : null}
      {(summary?.missingPriceEvents ?? 0) > 0 ? (
        <div className="warning">
          {t('usage.missingPriceWarning', { count: summary?.missingPriceEvents ?? 0 })}
        </div>
      ) : null}
      <section className="panel">
        <h2>{t('usage.columns')}</h2>
        <div className="usage-column-grid">
          {usageColumns.map((column) => (
            <label key={column.key} className="check-row">
              <input
                type="checkbox"
                checked={selectedColumnKeys.includes(column.key)}
                disabled={selectedColumnKeys.length === 1 && selectedColumnKeys.includes(column.key)}
                onChange={() => toggleColumn(column.key)}
              />
              <span>{column.header}</span>
            </label>
          ))}
        </div>
        {selectedColumnKeys.includes('userEmail') ? <p className="screen-help">{t('usage.userEmailCurrentNotice')}</p> : null}
      </section>
      {error === 'usage_window_too_large' ? (
        <section className="panel usage-placeholder">
          <h2>{t('usage.largeWindowTitle')}</h2>
          <p>{t('usage.largeWindowBody')}</p>
          <button type="button" onClick={shrinkRange}>
            {t('usage.shrinkToDefault')}
          </button>
        </section>
      ) : error ? (
        <div className="error">{error}</div>
      ) : null}
      <section className="usage-events-section">
        <div className="usage-events-heading">
          <h2>{t('usage.events')}</h2>
          <span>{t('usage.eventOrder')}</span>
        </div>
        <DataTable
          rows={events}
          empty={t('usage.noEvents')}
          pageSize={PAGE_SIZE}
          server={{ total: totalEvents, page, onPageChange: setPage }}
          getRowKey={(event) => event.id}
          columns={selectedColumns}
        />
      </section>
    </section>
  );
}

function UsageBars({
  points,
  usageKind,
  unit,
  empty
}: {
  points: UsageSeriesPoint[];
  usageKind: UsageKind | '';
  unit: string;
  empty: string;
}) {
  const values = points.map((point) => usageSeriesValue(point, usageKind));
  const max = Math.max(1, ...values);
  return (
    <div className="usage-series">
      {points.length ? (
        points.map((point, index) => (
          <div
            key={point.bucket}
            className="usage-series-row"
            aria-label={`${point.bucket}: ${formatInt((values[index] ?? 0))} ${unit}`}
          >
            <code>{point.bucket}</code>
            <div className="usage-series-track">
              <span style={{ width: `${Math.max(2, ((values[index] ?? 0) / max) * 100)}%` }} />
            </div>
            <span>{formatInt((values[index] ?? 0))}</span>
          </div>
        ))
      ) : (
        <div className="muted">{empty}</div>
      )}
    </div>
  );
}

function inputDateTime(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function readUsageColumns(): UsageColumnKey[] {
  if (typeof window === 'undefined') return DEFAULT_USAGE_COLUMNS;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(USAGE_COLUMN_STORAGE_KEY) ?? '[]') as unknown;
    if (!Array.isArray(parsed)) return DEFAULT_USAGE_COLUMNS;
    const selected = parsed.filter((key): key is UsageColumnKey => USAGE_COLUMN_KEYS.has(key as UsageColumnKey));
    return selected.length > 0 ? selected : DEFAULT_USAGE_COLUMNS;
  } catch {
    return DEFAULT_USAGE_COLUMNS;
  }
}

function daysBetween(from: string, to: string): number {
  if (!from || !to) return 0;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return (end - start) / (24 * 60 * 60 * 1000);
}

function formatDateRangeLabel(from: string, to: string, labels: { from: string; to: string; allTime: string }): string {
  const start = formatDateTimeLabel(from);
  const end = formatDateTimeLabel(to);
  if (start && end) return `${start} - ${end}`;
  if (start) return `${labels.from} ${start}`;
  if (end) return `${labels.to} ${end}`;
  return labels.allTime;
}

function formatDateTimeLabel(value: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function usageErrorCode(error: unknown): string {
  if (error && typeof error === 'object' && 'body' in error) {
    const body = (error as { body?: unknown }).body;
    if (typeof body === 'string') {
      try {
        return usageErrorCode({ body: JSON.parse(body) });
      } catch {
        return body;
      }
    }
    if (body && typeof body === 'object') {
      const payload = body as { error?: unknown; message?: unknown };
      if (payload.message === 'usage_window_too_large') return 'usage_window_too_large';
      if (typeof payload.message === 'string') return payload.message;
      if (typeof payload.error === 'string') return payload.error;
    }
  }
  return error instanceof Error ? error.message : 'usage_load_failed';
}

function displayCreditMillis(event: UsageEvent): number {
  return event.credit?.creditMillis ?? 0;
}

function formatCreditComponentUnits(
  event: UsageEvent,
  field: 'cachedUnits' | 'cacheWriteUnits' | 'nonCachedUnits',
  empty: string
): string {
  const component = event.credit?.components?.find((item) => item.kind === 'llm_tokens');
  const value = component?.[field];
  return value === undefined ? empty : formatInt(value);
}

function formatInt(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 }).format(value);
}

function usageSeriesValue(point: UsageSeriesPoint, usageKind: UsageKind | ''): number {
  if (usageKind === 'realtime_voice') return point.realtimeVoiceSessions ?? 0;
  if (usageKind === 'speech_to_text') return point.speechToTextRequests ?? 0;
  if (usageKind === 'text_to_speech') return point.textToSpeechRequests ?? 0;
  if (usageKind === 'web_search') return point.webSearchRequests;
  if (usageKind === 'image_generation') return point.imageGenerationCount;
  if (usageKind === 'video_generation') return point.videoGenerationSeconds;
  return point.totalTokens;
}

function formatCredits(value: number): string {
  return (value / 1_000).toFixed(3);
}
